<template>
    <div class="w-full">
        <div class="w-full border min-h-[260px] p-5 relative flex justify-center items-center bg-white "
            :class="{ 'border border-danger border-dashed': error, 'border-gray-300': !error }">
            <template v-if="!loading && files.length == 0">
                <div class="h-full w-full flex items-center bg-white justify-center flex-col cursor-pointer group"
                    @click="chooseFile()">
                    <nuxt-img loading="lazy" src="/icons/media-upload.png" alt="multimedia-upload" format="webp" :width="64" />
                    <p class="mt-2.5 mb-4 font-semibold group-hover:text-success text-base transition-colors ease-in duration-100">{{
                        dataLocales.data.title }}</p>
                    <div class="text-gray-700 text-sm text-center" v-html="dataLocales.data.description">

                    </div>
                    <div class=h-8>
                        <p class="text-sm text-danger mt-2" v-if="error">
                            <template v-if="error_message">
                                {{ error_message }}
                            </template>
                            <template v-else>
                                {{ dataLocales.data.err_msg }}
                            </template>
                        </p>
                    </div>
                </div>
            </template>
            <template v-else>
                <div
                    class="flex min-w-[280px] flex-col justify-center items-center text-center p-4 border border-dashed group border-white hover:border-gray-200 relative">
                    <template v-if="!loading && files[0]">
                        <div class="mx-auto mt-3 mb-6">
                            <nuxt-img loading="lazy" :src="'/types/' + icon + '.png'" width="60" height="80" />
                        </div>
                        <p class="line-clamp-1">{{ files[0].originalname }}</p>
                        <div class="text-sm text-grow">{{ utils.convertBytes(files[0].size ?? 0) }}</div>
                        <button @click="chooseFile()" v-if="!qrcreate.imageData"
                            class="mt-6 h-11 w-full bg-gray-200 text-black hover:bg-gray-300 flex items-center justify-center gap-2">
                            <TransformIcon stroke-width="1" /> <span>{{ $t('change_files') }}</span>
                        </button>
                        <XIcon v-if="!qrcreate.imageData" @click="DeleteFile(files[0])"
                            class="w-5 h-5 cursor-pointer text-gray-500 group-hover:block absolute top-1 right-1 hidden" />
                    </template>
                    <template v-else>
                        <div class="mx-auto mt-3 mb-6">
                            <nuxt-img loading="lazy" :src="'/types/' + icon + '.png'" width="60" height="80" />
                        </div>
                        <div class="w-[300px] bg-gray-200 h-2 rounded-md relative">
                            <span class="absolute left-0 top-0 h-2 bg-success rounded-md"
                                :style="{ 'width': percentage + '%' }"></span>
                        </div>
                        <div class="text-center py-2">
                            <IconsLoadingDecorator class="w-4" />  {{ dataLocales.data.loading }} ({{ percentage }})
                        </div>
                    </template>
                </div>
            </template>
            <input type="file" ref="file" accept="audio/*,video/*" @change="ChangeFile" class="hidden">
        </div>
        <partials-expired-file-notice />
        <PartialsLinkQrcode/>
        <tools-button-create @generate="generate" @clearSession="clearSession" />
    </div>
</template>
<script lang="ts">
import { useQrCreateStore } from "~/stores/qrcreate"
import uploadMixin from "./upload.mixin"
import { notify } from "@kyvg/vue3-notification"
import DataLocale from "~~/config/interface/DataLocale"
import { useLimit } from "~~/stores/limit"
export default defineComponent({
    mixins: [uploadMixin],
    data() {
        return {
            type: "multimedia",
            icon: ""
        }
    },
    setup() {
        const qrcreate = useQrCreateStore()
        const qrstyle = useQrStyle()
        const error = ref(false)
        const utils = useUtils();
        const dataLocales = new DataLocale([
            {
                locale: "en",
                title: "Upload audio & video",
                description: "<p>Uploading, storing and sharing unauthorized video and audio works is prohibited.</p> <p>The upload limit for a single file is 25M.</p>",
                err_msg: " Please click here to upload the file first",
                loading: " Uploading, please wait.."
            },
            {
                "locale": "es",
                "title": "Subir audio y video",
                "description": "<p>Está prohibido cargar, almacenar y compartir obras de audio y video no autorizadas.</p> <p>El límite de carga para un solo archivo es de 25M.</p>",
                "err_msg": "Haga clic aquí para cargar el archivo primero",
                "loading": "Cargando, por favor espere ..."
            }
            ,
            {
                "locale": "pt",
                "title": "Carregar áudio e vídeo",
                "description": "<p>O carregamento, armazenamento e compartilhamento de obras de vídeo e áudio não autorizadas é proibido.</p> <p>O limite de upload para um único arquivo é de 25M.</p>",
                "err_msg": "Clique aqui para carregar o arquivo primeiro",
                "loading": "Carregando, aguarde ..."
            }
            ,
            {
                "locale": "de",
                "title": "Audio & Video hochladen",
                "description": "<p>Das Hochladen, Speichern und Teilen von nicht autorisierten Video- und Audio-Werken ist verboten.</p> <p>Das Upload-Limit für eine einzelne Datei beträgt 25M.</p>",
                "err_msg": "Bitte klicken Sie hier, um zuerst die Datei hochzuladen",
                "loading": "Hochladen, bitte warten ..."
            },
            {
                "locale": "vi",
                "title": "Tải lên âm thanh & video",
                "description": "<p>Tải lên, lưu trữ và chia sẻ những tác phẩm video và âm thanh trái phép là không được phép.</p> <p>Giới hạn tải lên cho một tệp là 25M.</p>",
                "err_msg": "Vui lòng bấm vào đây để tải lên tệp",
                "loading": "Đang tải lên, vui lòng đợi..."
            },
            {
                "locale": "it",
                "title": "Carica audio & video",
                "description": "<p>Caricare, archiviare e condividere opere audio e video non autorizzate è proibito.</p> <p>Il limite di caricamento per un singolo file è di 25 MB.</p>",
                "err_msg": "Per favore, clicca qui per caricare il file prima",
                "loading": "Caricamento in corso, attendere..."
            }
            , {
                "locale": "fr",
                "title": "Télécharger des fichiers audio et vidéo",
                "description": "<p>Le téléchargement, le stockage et le partage d'œuvres audio et vidéo non autorisées sont interdits.</p> <p>La limite de téléchargement pour un seul fichier est de 25 Mo.</p>",
                "err_msg": "Veuillez cliquer ici pour télécharger le fichier d'abord",
                "loading": "Téléchargement en cours, veuillez patienter..."
            }
            , {
                "locale": "ja",
                "title": "ファイルをアップロードする",
                "description": "<p>複数の形式をサポートし、単一ファイルのアップロード制限は15Mです。</p>",
                "err_msg": "最初にここをクリックしてファイルをアップロードしてください。",
                "button": "ファイルを追加"
            }
            , {
                "locale": "zh",
                "title": "上传文件",
                "description": "<p>支持上传多种格式，单个文件上传限制为15M。</p>",
                "err_msg": "请先单击此处上传文件。",
                "button": "添加文件"
            },
            {
                "locale": "nl",
                "title": "Upload audio & video",
                "description": "<p>Het uploaden, opslaan en delen van ongeautoriseerde audio- en videowerken is verboden.</p><p>Het uploadlimiet voor een enkel bestand is 25 MB.</p>",
                "err_msg": "Klik hier om eerst het bestand te uploaden",
                "loading": "Uploaden, even geduld a.u.b.."
            }

        ])
        const limit = useLimit()
        return {
            qrcreate, qrstyle, error, error_message: ref(''), utils, dataLocales,limit
        }
    },
    methods: {
        chooseFile() {
            this.error = false;
            (this.$refs.file as HTMLInputElement).click()
        },
        validateData() {
           
            if (this.files.length) {
                return true;
            }
            this.error = true
            return false;
        },
        async generate() {
            if (this.validateData()) {
                await this.qrcreate.CreateSession(this.type, this.files)
            }
        },
        async ChangeFile(e: Event) {
            if (this.limit.checkLimit('multimedia')) {
                return
            }
            var files = (e.target as HTMLInputElement).files
            if (files?.length) {
                var formdata = new FormData()
                if (files[0].size > 25 * 1024 * 1024) {
                    notify({
                        type: "warn",
                        text: "Please reduce its size to less than 12mb",
                        title: "Sorry, the file has exceeded the upload limit",
                    })
                    return;
                }
                formdata.append('file', files[0])
                this.$forceUpdate()
                this.icon = this.DetectTypeFile(files[0])
                 
                if (this.files[0] && this.files[0].publicId) {
                    this.DeleteFile(this.files[0])
                }
                await this.UploadFiles(formdata, false, this.icon);
                (this.$refs.file as HTMLInputElement).value = ''
            }
        },
        DetectTypeFile(file: File) {
            if (file.type.includes('audio')) {
                return 'audio'
            }
            return 'video'
        }
    }
})
</script>